import { InMemoryWebStorage, WebStorageStateStore } from 'oidc-client-ts';
import { type AuthProviderProps, AuthProvider as OIDCAuthProvider, UserManager } from 'oidc-react';
import { dispatcherSettings } from './authDispatcher';

const isEmbedded = window.self !== window.top;

const authority = process.env.REACT_APP_AUTHORITY || 'https://login.microsoftonline.com/{tenant-id}';

/**
 * Guardian is embedded by two kinds of host. The Customer Portal signs its users in to the same
 * OneLogin as Guardian, so Guardian can sign in silently there. The product consoles (ARTESCA,
 * RING, ADI) sign their users in with Keycloak or Dex, so Guardian keeps its own sign-in there.
 */
export const isInCustomerPortal = (search: string, inFrame: boolean) =>
  inFrame && new URLSearchParams(search).get('source') === 'customer_portal';

export const createUserManager = ({ inCustomerPortal }: { inCustomerPortal: boolean }) =>
  new UserManager({
    authority,
    client_id: process.env.REACT_APP_CLIENT_ID || 'your-client-id',
    redirect_uri: process.env.REACT_APP_REDIRECT_URI || 'http://localhost:5173/auth/callback',
    response_type: 'code',
    scope: process.env.REACT_APP_SCOPE || 'openid profile params',
    loadUserInfo: false,
    // No silent_redirect_uri: a silent sign-in comes back to redirect_uri, the only URI OneLogin registers.
    automaticSilentRenew: true,
    revokeTokensOnSignout: true,
    revokeTokenAdditionalContentTypes: ['text/plain'],
    // The portal owns the session and Guardian signs in again on every load: a user kept in
    // sessionStorage would outlive the portal signing out.
    ...(inCustomerPortal && { userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }) }),
    ...dispatcherSettings(authority),
  });

export const userManager = createUserManager({
  inCustomerPortal: isInCustomerPortal(window.location.search, isEmbedded),
});

/** A hidden-iframe silent sign-in landing back on redirect_uri, rather than the app itself or a top-level sign-in. */
export const isSilentSignInCallback = (search: string, inFrame: boolean) => {
  const params = new URLSearchParams(search);
  return inFrame && params.has('state') && (params.has('code') || params.has('error'));
};

export const oidcConfig: AuthProviderProps = {
  userManager: userManager,
  autoSignIn: isEmbedded ? false : undefined,
  onBeforeSignIn: () => {
    localStorage.setItem('preAuthPath', window.location.pathname + window.location.search);
  },
  onSignIn: async (user: any) => {
    // You can add custom logic here after successful sign-in
    console.log('User signed in:', user);

    const redirectPath = localStorage.getItem('preAuthPath') || '/';
    localStorage.removeItem('preAuthPath');
    window.location.href = redirectPath;
  },
  onSignOut: async () => {
    // You can add custom logic here after sign-out
    console.log('User signed out');
  },
};

export default function AuthProvider({ children }: { children: React.ReactNode }) {
  return <OIDCAuthProvider {...oidcConfig}>{children}</OIDCAuthProvider>;
}
